import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Message} from "primereact/message";
import {Skeleton} from "primereact/skeleton";
import {useTranslate} from "../../system/useTranslate";
import {useTask} from "../../models/task/use-task";
import {useProject} from "../../models/project/use-project";
import {useGoal} from "../../models/goal/use-goal";
import {useReport} from "../../models/report/use-report";
import {useReportDashboard} from "../../models/report-dashboard/use-report-dashboard";
import DynamicReportBuilderDialog from "./dynamic-report-builder-dialog";
import ReportCard from "./report-card";
import {
    buildReportPayload,
    derivePortfolios,
    normalizeMembers,
    normalizeSavedReport,
    relationId,
    toArray,
    valueId,
} from "./report-utils";
import {RichTextView} from "../../system/components/rich-text";

function ReportDashboard() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const {documentId} = useParams();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const {user} = useSelector((state) => state.auth);
    const taskService = useTask();
    const projectService = useProject();
    const goalService = useGoal();
    const reportService = useReport();
    const dashboardService = useReportDashboard();
    const servicesRef = useRef({taskService, projectService, goalService});
    const reportServiceRef = useRef(reportService);
    const dashboardServiceRef = useRef(dashboardService);

    const workspace = workspaceMembershipData?.workspace;
    const workspaceId = workspace?.id;
    const workspaceFallback = useMemo(() => ({
        tasks: toArray(workspace?.tasks),
        projects: toArray(workspace?.projects),
        goals: toArray(workspace?.goals),
    }), [workspace]);

    const [builderVisible, setBuilderVisible] = useState(false);
    // the report being edited (null = a new one)
    const [editingReport, setEditingReport] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [savedReports, setSavedReports] = useState([]);
    const [reportsState, setReportsState] = useState({
        loading: false,
        error: false,
        saving: false,
        saveError: false,
    });
    const [dataState, setDataState] = useState({
        loading: false,
        error: false,
        tasks: [],
        projects: [],
        goals: [],
    });

    useEffect(() => {
        servicesRef.current = {taskService, projectService, goalService};
    }, [taskService, projectService, goalService]);

    useEffect(() => {
        reportServiceRef.current = reportService;
    }, [reportService]);

    useEffect(() => {
        dashboardServiceRef.current = dashboardService;
    }, [dashboardService]);

    useEffect(() => {
        let mounted = true;
        if (!workspaceId || !documentId) {
            setDashboard(null);
            setSavedReports([]);
            setReportsState((current) => ({...current, loading: false, error: false}));
            return () => {
                mounted = false;
            };
        }

        setReportsState((current) => ({...current, loading: true, error: false}));
        Promise.all([
            dashboardServiceRef.current.getReportDashboard(documentId),
            reportServiceRef.current.getReports(workspaceId, documentId),
        ]).then(([dashboardData, reportsData]) => {
            if (!mounted) return;
            setDashboard(dashboardData);
            setSavedReports(toArray(reportsData).length ? toArray(reportsData) : toArray(dashboardData?.reports));
            setReportsState((current) => ({...current, loading: false, error: false}));
        }).catch((error) => {
            if (!mounted) return;
            setDashboard(null);
            setSavedReports([]);
            setReportsState((current) => ({
                ...current,
                loading: false,
                error: error?.message || true,
            }));
        });

        return () => {
            mounted = false;
        };
    }, [workspaceId, documentId]);

    useEffect(() => {
        let mounted = true;
        if (!workspaceId) {
            setDataState({
                loading: false,
                error: false,
                ...workspaceFallback,
            });
            return () => {
                mounted = false;
            };
        }

        setDataState((current) => ({
            ...current,
            loading: true,
            error: false,
        }));

        const services = servicesRef.current;
        Promise.all([
            services.taskService.getTasks(null, workspaceId),
            services.projectService.getProjects(workspaceId, true),
            services.goalService.getGoals(workspaceId),
        ]).then(([tasks, projects, goals]) => {
            if (!mounted) return;
            setDataState({
                loading: false,
                error: false,
                tasks: toArray(tasks).length ? toArray(tasks) : workspaceFallback.tasks,
                projects: toArray(projects).length ? toArray(projects) : workspaceFallback.projects,
                goals: toArray(goals).length ? toArray(goals) : workspaceFallback.goals,
            });
        }).catch(() => {
            if (!mounted) return;
            setDataState({
                loading: false,
                error: true,
                ...workspaceFallback,
            });
        });

        return () => {
            mounted = false;
        };
    }, [workspaceId, workspaceFallback]);

    const reportDataContext = useMemo(() => {
        const projects = dataState.projects.length ? dataState.projects : workspaceFallback.projects;
        return {
            tasks: dataState.tasks.length ? dataState.tasks : workspaceFallback.tasks,
            projects,
            goals: dataState.goals.length ? dataState.goals : workspaceFallback.goals,
            portfolios: derivePortfolios(projects),
            members: normalizeMembers(workspace),
            currentUser: workspaceMembershipData,
            loading: dataState.loading,
            error: dataState.error,
        };
    }, [dataState, user, workspace, workspaceFallback]);

    const reports = useMemo(() => {
        return savedReports.map((report) => normalizeSavedReport(report, translate));
    }, [savedReports, translate]);

    const openBuilder = (report = null) => {
        setEditingReport(report);
        setBuilderVisible(true);
    };
    const closeBuilder = () => {
        setBuilderVisible(false);
        setEditingReport(null);
    };

    const handleUpdateReport = async ({config, preview, rowsCount}) => {
        const reportId = editingReport?.documentId || editingReport?.id;
        if (!reportId) return;
        setReportsState((current) => ({...current, saving: true, saveError: false}));
        try {
            const payload = buildReportPayload({config, preview, rowsCount, translate, forUpdate: true});
            const updatedReport = await reportServiceRef.current.updateReport(reportId, payload);
            setSavedReports((current) => current.map((item) => (
                valueId(item) === valueId(editingReport) ? {...item, ...(updatedReport || payload)} : item
            )));
            setReportsState((current) => ({...current, saving: false, saveError: false}));
            closeBuilder();
        } catch (error) {
            setReportsState((current) => ({
                ...current,
                saving: false,
                saveError: error?.message || translate("reports.page.saveError"),
            }));
        }
    };

    const handleCreateReport = async ({config, preview, rowsCount}) => {
        if (!workspaceId || !dashboard) {
            setReportsState((current) => ({...current, saveError: translate("reports.dashboard.noWorkspace")}));
            return;
        }

        setReportsState((current) => ({...current, saving: true, saveError: false}));
        try {
            const payload = buildReportPayload({
                config,
                preview,
                rowsCount,
                workspaceId,
                reportDashboardId: relationId(dashboard),
                translate,
            });
            const createdReport = await reportServiceRef.current.createReport(payload);
            setSavedReports((current) => [createdReport || payload, ...current]);
            setReportsState((current) => ({...current, saving: false, saveError: false}));
            closeBuilder();
        } catch (error) {
            setReportsState((current) => ({
                ...current,
                saving: false,
                saveError: error?.message || translate("reports.page.saveError"),
            }));
        }
    };

    return (
        <div className="p-3 md:p-4 surface-ground min-h-screen" dir="rtl">
            <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3 mb-4">
                <div className="flex flex-column gap-2 text-right">
                    <Button
                        label={translate("reports.dashboard.back")}
                        icon="pi pi-angle-right"
                        text
                        size="small"
                        className="align-self-start p-0"
                        onClick={() => navigate("/reports")}
                    />
                    {reportsState.loading ? (
                        <Skeleton width="18rem" height="2.25rem"/>
                    ) : (
                        <h1 className="m-0 text-3xl font-bold text-900">{dashboard?.title || translate("reports.dashboard.notFound")}</h1>
                    )}
                    <RichTextView className="m-0 text-base line-height-3 text-700" value={dashboard?.description}/>
                </div>
                <Button
                    label={translate("reports.dashboard.addReport")}
                    icon="pi pi-plus"
                    onClick={() => openBuilder()}
                    size="small"
                    className="text-xs"
                    loading={reportsState.saving}
                    disabled={!dashboard || reportsState.loading}
                />
            </div>

            {dataState.error && (
                <Message severity="warn" text={translate("reports.page.dataWarning")} className="mb-3 text-xs"/>
            )}

            {reportsState.error && (
                <Message severity="error" text={translate("reports.dashboard.loadError")} className="mb-3 text-xs"/>
            )}

            {reportsState.saveError && (
                <Message
                    severity="error"
                    text={reportsState.saveError === true ? translate("reports.page.saveError") : reportsState.saveError}
                    className="mb-3 text-xs"
                />
            )}

            <div className="flex align-items-center justify-content-between gap-2 mb-3 pb-3 border-bottom-1 border-200">
                <div className="flex align-items-center gap-2 text-900">
                    <i className="pi pi-chart-line text-primary"/>
                    <h2 className="m-0 text-xl font-bold">{translate("reports.dashboard.reportsTitle")}</h2>
                </div>
            </div>

            {(reportsState.loading || dataState.loading) && (
                <div className="grid">
                    {[1, 2, 3].map((item) => (
                        <div className="col-12 md:col-6 lg:col-4" key={item}>
                            <div className="surface-card border-1 border-200 border-round p-3 h-full flex flex-column gap-3">
                                <Skeleton width="3rem" height="3rem"/>
                                <Skeleton width="70%" height="1.25rem"/>
                                <Skeleton width="90%" height="1rem"/>
                                <Skeleton width="100%" height="8rem"/>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!reportsState.loading && !dataState.loading && (
                <div className="grid">
                    <div className="col-12 md:col-6 lg:col-4">
                        <Card className="h-full border-1 border-dashed border-300 shadow-none">
                            <div className="flex flex-column align-items-center justify-content-center gap-2 text-center p-3">
                                <Button
                                    icon="pi pi-plus"
                                    rounded
                                    outlined
                                    size="small"
                                    aria-label={translate("reports.dashboard.emptyReports.title")}
                                    onClick={() => openBuilder()}
                                    loading={reportsState.saving}
                                    disabled={!dashboard}
                                />
                                <strong className="text-sm font-medium text-900">{translate("reports.dashboard.emptyReports.title")}</strong>
                                <small className="text-xs text-600">{translate("reports.dashboard.emptyReports.description")}</small>
                            </div>
                        </Card>
                    </div>
                    {reports.map((report) => (
                        <div className="col-12 md:col-6 lg:col-4" key={valueId(report)}>
                            <ReportCard report={report} reportDataContext={reportDataContext} translate={translate} onEdit={report.system ? undefined : openBuilder}/>
                        </div>
                    ))}
                </div>
            )}

            <DynamicReportBuilderDialog
                visible={builderVisible}
                onHide={closeBuilder}
                dataContext={reportDataContext}
                report={editingReport}
                onSubmit={editingReport ? handleUpdateReport : handleCreateReport}
                saving={reportsState.saving}
            />
        </div>
    );
}

export default ReportDashboard;
